/**
 * Game configuration table. Adding a game means adding one row here.
 *
 * `sharingExposure` is the share of expected prize value that is parimutuel, i.e.
 * split between co-winners and therefore sensitive to how popular a combination is.
 *   1.0 = every rank is a shared pool      -> avoiding popular combinations pays
 *   0.0 = every rank is a fixed amount     -> avoiding popular combinations pays nothing
 * Lambda is DERIVED from it, never chosen by hand.
 */

export type GameKey = 'loto' | 'em' | 'eurodreams';

/** ISO weekday numbers: 1 = Monday ... 7 = Sunday. */
export type Weekday = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export interface GameConfig {
  readonly key: GameKey;
  readonly label: string;
  /** k main numbers drawn from 1..n */
  readonly n: number;
  readonly k: number;
  /** bk bonus numbers drawn from 1..bmax */
  readonly bmax: number;
  readonly bk: number;
  readonly bonusLabel: string;
  readonly days: readonly Weekday[];
  /** Draw time, Europe/Paris, as [hour, minute]. */
  readonly drawTime: readonly [number, number];
  /** Price of one grid, in euros. */
  readonly price: number;
  readonly sharingExposure: number;
  /** 1 in `topOdds` chance of hitting rank 1. */
  readonly topOdds: number;
  /** Return to player, as a fraction. null when not published reliably. */
  readonly rtp: number | null;
  /** Order of magnitude of grids played per draw. Used for the co-winner model. */
  readonly typicalVolume: number;
  /**
   * How strongly the popularity index actually predicts what French players pick.
   *
   * MEASURED, not assumed: `npm run calibrate` regresses the published winner count
   * on the popularity index of the winning combination, over the whole archive, with
   * a low rank as an offset for ticket volume. 1.0 would mean the imported
   * coefficients describe French players exactly; the fitted value is well below.
   *
   * The generator does not use this. Ranking combinations by popularity is unchanged
   * by a monotone transform, so the grids produced are the same either way. It
   * changes only what the application CLAIMS the benefit is worth.
   */
  readonly popularityElasticity: number;
  /**
   * Return to player actually observed, not the operator's published figure.
   * Derived by `npm run rtp` from the payout reports: total redistributed divided
   * by total staked, the stake reconstructed from winner counts and combinatorics.
   */
  readonly measuredRtp: number;
  /** Set when the measured figure is a floor rather than an estimate. */
  readonly measuredRtpCaveat: string | null;
  /**
   * Return observed by size of the announced rank-1 prize. This is the ONLY lever
   * on expected return the data supports, and it exists only where the operator
   * publishes the announced jackpot for every draw — which the Loto does and the
   * two European games do not. null means "not measurable", never "no effect".
   */
  readonly jackpotBands: readonly { readonly fromCents: number; readonly rtp: number }[] | null;
  /** Derived: 1.6 x sharingExposure, rounded to 3 decimals. */
  readonly lambda: number;
  readonly notes: string;
}

const LAMBDA_BASE = 1.6;

/** Same rounding as Python's round(x, 3) for the values used here. */
function round3(x: number): number {
  return Math.round(x * 1000) / 1000;
}

function game(
  cfg: Omit<GameConfig, 'lambda'>,
): GameConfig {
  return { ...cfg, lambda: round3(LAMBDA_BASE * cfg.sharingExposure) };
}

export const GAMES: { readonly [K in GameKey]: GameConfig } = {
  loto: game({
    key: 'loto',
    label: 'Loto',
    n: 49,
    k: 5,
    bmax: 10,
    bk: 1,
    bonusLabel: 'N° Chance',
    days: [1, 3, 6],
    drawTime: [20, 45],
    price: 2.2,
    sharingExposure: 1.0,
    topOdds: 19_068_840,
    rtp: 0.544,
    typicalVolume: 9_000_000,
    // b = 0,561 sur l'ere courante (IC95 0,502-0,619), 0,600 toutes eres.
    // 1112 tirages, 2508 gagnants au rang « 5 numeros ». Offset : rang 9,
    // le remboursement au n° Chance seul, qui ne depend pas des numeros tires.
    popularityElasticity: 0.561,
    // 1113 tirages, 10 881 M€ mises reconstituees, 5 647 M€ redistribues.
    measuredRtp: 0.519,
    measuredRtpCaveat: null,
    // Un jackpot gonfle contient les mises des tirages precedents, que le joueur
    // du soir n'a pas payees. L'ecart est de 24 points entre les terciles.
    jackpotBands: [
      { fromCents: 0, rtp: 0.411 },
      { fromCents: 400_000_000, rtp: 0.451 },
      { fromCents: 900_000_000, rtp: 0.653 },
    ],
    notes: 'Tous les rangs sont des cagnottes parimutuelles.',
  }),
  em: game({
    key: 'em',
    label: 'EuroMillions',
    n: 50,
    k: 5,
    bmax: 12,
    bk: 2,
    bonusLabel: 'Étoiles',
    days: [2, 5],
    drawTime: [21, 30],
    price: 2.5,
    sharingExposure: 1.0,
    topOdds: 139_838_160,
    rtp: 0.5,
    typicalVolume: 75_000_000,
    // b = 0,296 (IC95 0,228-0,364), stable a 0,297 toutes eres, 688 tirages.
    // BORNE INFERIEURE : l'offset disponible (rang 13, deux numeros) depend
    // legerement de la combinaison, ce qui tire la pente vers le bas.
    popularityElasticity: 0.296,
    // 689 tirages, 9 627 M€ mises reconstituees, 4 487 M€ redistribues.
    measuredRtp: 0.466,
    measuredRtpCaveat: null,
    // Le montant du rang 1 n'est publie que lorsqu'un Francais l'emporte, soit 16 %
    // des tirages. Grouper dessus conditionnerait sur le gain et donnerait un
    // retour superieur a 100 %, qui serait un artefact de selection.
    jackpotBands: null,
    notes: 'Tous les rangs sont des cagnottes parimutuelles.',
  }),
  eurodreams: game({
    key: 'eurodreams',
    label: 'EuroDreams',
    n: 40,
    k: 6,
    bmax: 5,
    bk: 1,
    bonusLabel: 'N° Dream',
    days: [1, 4],
    drawTime: [21, 0],
    price: 2.5,
    sharingExposure: 0.02,
    topOdds: 19_191_900,
    rtp: null,
    typicalVolume: 3_500_000,
    // NON MESURABLE : 64 gagnants en tout sur les rangs « 6 numeros » depuis 2023,
    // IC95 [-0,30 ; 0,46]. On reprend l'estimation du Loto, meme population de
    // joueurs, plutot que de publier du bruit. De toute facon l'exposition au
    // partage de 0,02 rend le gain reel negligeable ici.
    popularityElasticity: 0.561,
    // 1113 tirages, 10 881 M€ mises reconstituees, 5 647 M€ redistribues.
    measuredRtp: 0.519,
    measuredRtpCaveat: null,
    // Un jackpot gonfle contient les mises des tirages precedents, que le joueur
    // du soir n'a pas payees. L'ecart est de 24 points entre les terciles.
    jackpotBands: [
      { fromCents: 0, rtp: 0.411 },
      { fromCents: 400_000_000, rtp: 0.451 },
      { fromCents: 900_000_000, rtp: 0.653 },
    ],
    notes:
      'Rangs à montant fixe : rente de 20 000 €/mois au rang 1 (jusqu’à trois gagnants ' +
      'simultanés, chacun servi en entier), 2 000 €/mois au rang 2, montants fixes ensuite. ' +
      'Le partage n’intervient qu’au-delà de trois gagnants au rang 1. Éviter les ' +
      'combinaisons populaires n’y rapporte donc quasiment rien.',
  }),
};

export const GAME_KEYS: readonly GameKey[] = ['loto', 'em', 'eurodreams'];

export function isGameKey(value: string): value is GameKey {
  return (GAME_KEYS as readonly string[]).includes(value);
}

/** Number of distinct rank-1 combinations: C(n,k) x C(bmax,bk). */
export function totalCombinations(g: GameConfig): number {
  return binomial(g.n, g.k) * binomial(g.bmax, g.bk);
}

export function binomial(n: number, k: number): number {
  if (k < 0 || k > n) return 0;
  let r = 1;
  for (let i = 1; i <= k; i++) r = (r * (n - k + i)) / i;
  return Math.round(r);
}
